import { Button, Spin, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';

import ActiveProducerBanner from '../../components/producer-management/activeProducerBanner';
import AddProducerModal from '../../components/producer-management/addProducerModal';
import EditProducerModal from '../../components/producer-management/editProducerModal';
import { Helmet } from 'react-helmet';
import { IsAdminProp } from '../../types/props/isAdmin.props';
import { ProducerDTO } from '../../types/data/producer-management.type';
import { ProducerService } from '../../service/producer-management.service';
import SecureComponent from '../../components/authorization';
import TableComponent from '../../components/table';
import { TableType } from '../../enums/table-type.enum';

const ProducerManagement: React.FC<IsAdminProp> = () => {
  const [producers, setProducers] = useState<ProducerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedProducer, setSelectedProducer] = useState<ProducerDTO | null>(
    null
  );

  const loadProducers = async () => {
    setLoading(true);
    try {
      const data = await ProducerService.getAllProducers();
      setProducers(data);
    } catch (error: any) {
      message.error('Failed to load producers');
      console.error('Error loading producers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProducer = () => {
    setAddModalVisible(true);
  };

  const handleEditProducer = (producer: ProducerDTO) => {
    setSelectedProducer(producer);
    setEditModalVisible(true);
  };

  const handleDeleteProducer = async (producerId: string) => {
    try {
      await ProducerService.deleteProducer(producerId);
      message.success('Producer deleted successfully');
      loadProducers();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to delete producer'
      );
    }
  };

  const handleAddSuccess = () => {
    setAddModalVisible(false);
    loadProducers();
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedProducer(null);
    loadProducers();
  };

  const handleAddCancel = () => {
    setAddModalVisible(false);
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSelectedProducer(null);
  };

  // Find the active producer
  const activeProducer =
    producers.find(producer => producer.isProducerActiveForEApp) || null;

  useEffect(() => {
    loadProducers();
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Producer Management | Quoting Tool SA Dashboard | Elite Insurance
          Partners
        </title>
        <meta
          name='description'
          content={`Elite Insurance Partners | Quoting Tool SA Dashboard - Producer Management`}
        />
      </Helmet>

      <div>
        <div>
          <Typography.Title level={3}>Producer Management</Typography.Title>
        </div>

        {/* Active Producer Banner */}
        <ActiveProducerBanner
          activeProducer={activeProducer}
          onEdit={handleEditProducer}
        />

        <div
          style={{
            background: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '20px',
            margin: '20px 0px'
          }}>
          <Button type='primary' onClick={handleAddProducer} disabled={loading}>
            + Add new producer
          </Button>
        </div>

        {/* Producers Table */}
        <div style={{ background: '#ffffff', padding: '20px' }}>
          {loading && producers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size='large' />
            </div>
          ) : (
            <TableComponent
              data={producers}
              tableType={TableType.PRODUCER_MANAGEMENT}
              loading={loading}
              handleEdit={handleEditProducer}
              handleRemove={handleDeleteProducer}
              pagination={true}
              disabled={false}
            />
          )}
        </div>

        {/* Modals */}
        <AddProducerModal
          visible={addModalVisible}
          onCancel={handleAddCancel}
          onSuccess={handleAddSuccess}
        />

        <EditProducerModal
          visible={editModalVisible}
          producer={selectedProducer}
          onCancel={handleEditCancel}
          onSuccess={handleEditSuccess}
        />
      </div>
    </>
  );
};

export default SecureComponent(ProducerManagement, false);
